import { CropStyleFillPattern, GrassCuts, GrassManagement } from "@omnia/shared/generated/enums";
import { RGBColor } from "@omnia/shared/types";
import { FieldViewModel, LocaleString, str } from "../omnia";
import { StylesApi } from "./api";
import { getMasterStyles } from "./defaults";

export interface State {
    readonly api: StylesApi;

    /**
     * The full range of years that could be selected.
     */
    readonly availableYears: number[];

    /**
     * The current year.
     */
    readonly currentYear: number;

    /**
     * The currently-selected years.
     */
    readonly selectedYears: number[];

    /**
     * The initially-selected range of years.
     */
    readonly initialYears: number[];

    readonly cropping: FieldViewModel[];
    readonly masterStyles: ReturnType<typeof getMasterStyles>;

    readonly crops: CropStyle[];
}

export interface CropStyle extends BasicStyleWithInitialState {
    /**
     * The `Crop` ID.
     */
    readonly cropId: number;

    /**
     * The `Crop` name.
     */
    readonly name: string;

    readonly varieties: VarietyStyle[];

    readonly purposes: PurposeStyle[];

    readonly grasses: GrassStyle[];

    readonly descriptions: DescriptionStyle[];

    /**
     * The system/default style for the `Crop`
     */
    readonly defaultStyle: Readonly<BasicStyle>;
}

export interface VarietyStyle extends BasicStyleWithInitialState {
    /**
     * The `CropVariety` ID.
     */
    readonly varietyId: number;

    /**
     * The `CropVariety` name.
     */
    readonly name: string;

    /**
     * The system/default style for the `CropVariety`
     */
    readonly defaultStyle: Readonly<BasicStyle>;
}

export interface PurposeStyle extends BasicStyleWithInitialState {
    /**
     * The `CropPurpose` enum value.
     */
    readonly purpose: number;

    /**
     * The system/default style for the `CropPurpose` (currently always empty, there are currently no default styles for `CropPurpose`)
     */
    readonly defaultStyle: Readonly<BasicStyle>;
}

export interface GrassStyle extends BasicStyleWithInitialState {
    readonly grassManagement: GrassManagement;
    readonly grassCuts: GrassCuts | null;

    /**
     * The system/default style for the `Grass` (currently always empty, there are currently no default styles for `Grass`)
     */
    readonly defaultStyle: Readonly<BasicStyle>;
}

export interface DescriptionStyle extends BasicStyleWithInitialState {
    readonly description: string;

    /**
     * The system/default style for the `Description` (currently always empty, there are currently no default styles for `Description`)
     */
    readonly defaultStyle: Readonly<BasicStyle>;
}

export interface BasicStyleWithInitialState extends BasicStyle {
    /**
     * The initial value of this style.
     */
    readonly initialState: Readonly<BasicStyle>;
}

export interface BasicStyle {
    color: RGBColor;
    pattern: CropStyleFillPattern;
}

/**
 * Represents a `HoldingCropStyle` retrieved from the API.
 */
export interface ApiStyle extends BasicStyle {
    readonly cropId: number;
    readonly year: number;
    readonly varietyId?: number;
    readonly purpose?: number;
    readonly grassManagement?: GrassManagement;
    readonly grassCuts?: GrassCuts;
    readonly croppingCropDescr?: string;
}

export interface ApiCropStyleUpdate {
    readonly cropId: number;

    readonly years: number[];

    readonly color: RGBColor;
    readonly pattern: CropStyleFillPattern;

    readonly varieties: ApiVarietyStyleUpdate[];

    readonly purposes: ApiPurposeStyleUpdate[];

    readonly grasses: ApiGrassStyleUpdate[];

    readonly descriptions: ApiDescriptionStyleUpdate[];
}

interface ApiStyleUpdate {
    readonly color: RGBColor;
    readonly pattern: CropStyleFillPattern;
}

export interface ApiVarietyStyleUpdate extends ApiStyleUpdate {
    readonly varietyId: number;
}

export interface ApiDescriptionStyleUpdate extends ApiStyleUpdate {
    readonly description: string;
}

export interface ApiPurposeStyleUpdate extends ApiStyleUpdate {
    readonly purpose: number;
}

export interface ApiGrassStyleUpdate extends ApiStyleUpdate {
    readonly grassManagement: GrassManagement;
    readonly grassCuts: GrassCuts;
}

export const PATTERNS: { id: CropStyleFillPattern; text: LocaleString }[] = [
    { id: CropStyleFillPattern.None, text: str.None },
    { id: CropStyleFillPattern.Horizontal, text: "Horizontal" as LocaleString },
    { id: CropStyleFillPattern.Vertical, text: "Vertical" as LocaleString },
    { id: CropStyleFillPattern.LeftDiagonal, text: "Left diagonal" as LocaleString },
    {
        id: CropStyleFillPattern.RightDiagonal,
        text: "Right diagonal" as LocaleString,
    },
];

/**
 * Array of pattern options to use in pattern select.
 */
export const PATTERN_OPTIONS: (readonly [CropStyleFillPattern, LocaleString])[] = PATTERNS.map((p) => [p.id, p.text]);
